import React, { Component } from 'react';
import { CaseChartPanel } from '../../../../../domain/case/CaseDomains';
import FormicaLabel from '../../../../custom/FormicaLabel';
import FormicaSearchSelectBox, { FormicaSelectBoxItem } from '../../../../custom/FormicaSearchSelectBox';
import { query, repository, subscribe } from '@trlogic/client-js';
import { InstanceContainer } from '../../../../../config/binding/InstanceBinding';
import Injectables from '../../../../../constant/Injectables';
import CollectionNames from '../../../../../constant/CollectionNames';
import { RootState } from '../../../../../redux/store';
import { connect } from 'react-redux';
import { ConnectedProps } from 'react-redux/es/exports';
import { AuthState } from '../../../../../redux/reducers/AuthReducer';
import { ChartJsonModel } from '../../../../../domain/chart/ChartDomains';
import { TransactionJsonModel, TransactionVariableJsonModel } from '../../../../../domain/stream/transaction/TransactionDomains';
import { ConfigurationState } from '../../../../../redux/reducers/ConfigurationReducer';
import DataType from '../../../../../constant/DataType';
import eq = query.eq;
import ComponentName from '../../../../../constant/ComponentName';
import LanguageUtil from '../../../../../util/LanguageUtil';

class DomainConfigurationChartPanelComponent extends Component<Props, State> {
  public state = new State();

  private transactionSubscription: subscribe.Subscription | undefined;
  private chartSubscription: subscribe.Subscription | undefined;
  private computation: subscribe.Computation | undefined;
  private repository: repository.ReadOnlyQueryableRepository = InstanceContainer.get(Injectables.REPOSITORY);

  public render() {
    const { panel, index } = this.props;

    return (
      <div>
        <div className={'flex w-full flex-col  px-5 py-2 overflow-y-scroll'}>
          <FormicaLabel label={LanguageUtil.get({ id: 'common.label.dashboard', defaultMessage: 'Dashboard' })} />
          <FormicaSearchSelectBox
            value={panel.selectedDashboard}
            rounded
            placeholder={LanguageUtil.get({ id: 'common.input.placeholder.dashboard', defaultMessage: 'Dashboard' })}
            options={this.getDashboardOptions()}
            onChange={this.onChangeDashboard}
            data-testid={`${ComponentName.CONFIGURATION}_panel_${index}_dashboard_select`}
          />
        </div>
        <div className={'flex w-full flex-col  px-5 py-2'}>
          <FormicaLabel label={LanguageUtil.get({ id: 'common.label.chart', defaultMessage: 'Chart' })} />
          <FormicaSearchSelectBox
            value={panel.selectedChart}
            rounded
            options={this.getChartOptions()}
            onChange={this.onChangeChart}
            placeholder={LanguageUtil.get({ id: 'common.input.placeholder.chart', defaultMessage: 'Chart' })}
            data-testid={`${ComponentName.CONFIGURATION}_panel_${index}_chart_select`}
          />
        </div>
        <div className={'flex w-full flex-col  px-5 py-2'}>
          <FormicaLabel label={LanguageUtil.get({ id: 'common.label.filterWith', defaultMessage: 'filterWith' })} />
          <FormicaSearchSelectBox
            value={panel.filterWith.value}
            rounded
            onChange={this.onChangeFilterWith}
            options={this.getTransactionVariables()}
            placeholder={LanguageUtil.get({ id: 'common.input.placeholder.filterWith', defaultMessage: 'Filter With' })}
            data-testid={`${ComponentName.CONFIGURATION}_panel_${index}_filter_select`}
          />
        </div>
        <div className={'flex w-full flex-col  px-5 py-2'}>
          <FormicaLabel label={LanguageUtil.get({ id: 'common.label.chartFilterField', defaultMessage: 'Chart Filter Field' })} />
          <FormicaSearchSelectBox
            value={panel.chartFilterField.value}
            rounded
            onChange={this.onChangeChartFilterField}
            options={this.getTransactionVariables()}
            placeholder={LanguageUtil.get({ id: 'common.input.placeholder.chartFilterField', defaultMessage: 'Chart Filter Field' })}
            data-testid={`${ComponentName.CONFIGURATION}_panel_${index}_filter_field_select`}
          />
        </div>
      </div>
    );
  }

  public componentDidMount() {
    this.startTracker();
  }

  public componentWillUnmount() {
    this.stopTracker();
  }

  public startTracker = async () => {
    const transactionQuery = query.QueryBuilder.select().from(CollectionNames.TRANSACTIONS);
    const chartQuery = query.QueryBuilder.select()
      .from(CollectionNames.CHARTS)
      .where(eq('dashboardId', this.props.panel.selectedDashboard + '_' + this.props.user?.user?.activeRiskType + '_dashboard'));
    this.transactionSubscription = subscribe.Formica.subscribe(CollectionNames.DASHBOARDS, transactionQuery.build());
    this.chartSubscription = subscribe.Formica.subscribe(CollectionNames.CHARTS, chartQuery.build());
    this.computation = subscribe.Tracker.autorun(this.tracker, [this.transactionSubscription, this.chartSubscription]);
  };

  public tracker = async () => {
    const {
      user: { user },
      panel,
    } = this.props;
    if (this.transactionSubscription?.ready() && this.chartSubscription?.ready()) {
      const transactionQuery: query.Query = query.QueryBuilder.select().from(CollectionNames.TRANSACTIONS).select('id', 'name', 'variables').build();

      const chartQuery: query.Query = query.QueryBuilder.select()
        .from(CollectionNames.CHARTS)
        .select('name', 'id')
        .where(eq('dashboardId', panel.selectedDashboard + '_' + user?.activeRiskType + '_dashboard'))
        .build();

      const transactions: TransactionJsonModel[] | undefined = await this.repository.findMany(transactionQuery);
      const charts: ChartJsonModel[] | undefined = await this.repository.findMany(chartQuery);
      this.setState({ transactions, charts });
    }
  };

  public stopTracker = async () => {
    await this.transactionSubscription?.stop();
    await this.chartSubscription?.stop();
    this.computation?.stop();
  };

  public resubscribeTracker = async () => {
    this.setState({ loading: true });
    if (this.computation == undefined) {
      await this.startTracker();
    } else {
      this.computation?.onStop(this.startTracker);
      await this.stopTracker();
    }
  };

  private onChangeFilterWith = (e: FormicaSelectBoxItem) => {
    const { panel } = this.props;
    panel.filterWith.value = e.value;
    panel.filterWith.dataType = e.dataType as DataType;
    this.props.changePanel();
  };

  private onChangeChartFilterField = (e: FormicaSelectBoxItem) => {
    const { panel } = this.props;
    panel.chartFilterField.value = e.value;
    panel.chartFilterField.dataType = e.dataType as DataType;
    this.props.changePanel();
  };

  private getTransactionVariables = (): FormicaSelectBoxItem[] => {
    const { transactions } = this.state;
    const transaction = transactions.find((transaction: TransactionJsonModel) => transaction.name == this.props.panel.selectedDashboard);
    if (transaction) {
      return transaction.variables.map((variable: TransactionVariableJsonModel) => {
        return {
          label: variable.name,
          value: variable.name,
          dataType: variable.properties.dataType,
        };
      });
    }
    return [];
  };

  private getDashboardOptions = (): FormicaSelectBoxItem[] => {
    const { transactions } = this.state;
    const options: FormicaSelectBoxItem[] = transactions.map((transaction: TransactionJsonModel) => {
      return {
        label: (transaction.name as string) + ' Dashboard',
        value: transaction.name,
      };
    });
    return options;
  };

  private onChangeDashboard = (e: FormicaSelectBoxItem) => {
    const { panel } = this.props;
    panel.selectedDashboard = e.value;
    this.props.changePanel();
    this.resubscribeTracker();
  };

  private onChangeChart = (e: FormicaSelectBoxItem) => {
    const { panel } = this.props;
    panel.selectedChart = e.value;
    this.props.changePanel();
  };

  private getChartOptions = (): FormicaSelectBoxItem[] => {
    const { charts } = this.state;
    return charts.map((chart: ChartJsonModel) => {
      return {
        label: chart.name,
        value: chart.id,
      };
    });
  };
}

interface Props extends ReduxProps {
  deletePanel: (panel: CaseChartPanel) => void;
  changePanel: () => void;
  panel: CaseChartPanel;
  disabled: boolean;
  index: number;
}

class State {
  transactions: TransactionJsonModel[] = [];
  charts: ChartJsonModel[] = [];
  loading: boolean = true;
}

const mapStateToProps = (state: RootState) => ({
  user: state.auth as AuthState,
  configuration: state.configuration as ConfigurationState,
});
const mapDispatchToProps = {};
const connector = connect(mapStateToProps, mapDispatchToProps);
type ReduxProps = ConnectedProps<typeof connector>;

export default connector(DomainConfigurationChartPanelComponent);
